import { useMemo } from 'react';
import { CustomFieldDefinition, CustomFieldUsage } from '@alucio/aws-beacon-amplify/src/models';
import { StaticFields } from 'src/components/CustomFields/ComposableForm';

import { beaconConfig, getMeetingDefaultValues, getSeparatedAttendees } from './AddMeetingConfig';
import onGoingMeetingConfig, { initialDefaultValues } from './MeetingDetailsConfig';
import { CustomFieldValuesMap, MeetingORM } from 'src/types/orms';
import { useMeeting } from 'src/state/redux/selector/meeting';
import { useCRMFormConfig } from 'src/state/redux/selector/crm';
import { useTenantCustomFields } from 'src/state/redux/selector/tenant';
import { useCRMStatus } from 'src/hooks/useCRMStatus';
import { getMappedCustomValues } from 'src/state/redux/selector/common';
import merge from 'lodash/merge';
import { useIsMeetingLocked } from 'src/components/Meeting/AddMeetingProvider';
import cloneDeep from 'lodash/cloneDeep';

export interface MeetingFormConfig {
  customFields: CustomFieldDefinition[],
  meetingORM?: MeetingORM,
  staticFields: StaticFields,
  staticValues: CustomFieldValuesMap,
  values: CustomFieldValuesMap,
  isLoading: boolean,
  isReadOnly: boolean,
}

const useMeetingFormConfig = (meetingId?: string, isOngoingMeeting?: boolean):
  MeetingFormConfig => {
  const meetingORM = useMeeting(meetingId ?? 'placeholder');
  const { canUseMeetingCRMFields, isCRMConfigEnabled } = useCRMStatus();
  const customFields = useTenantCustomFields({ usages: { internalUsages: [CustomFieldUsage.MEETING] } });
  const defaultValues = getMeetingDefaultValues(customFields, meetingORM, isOngoingMeeting, isCRMConfigEnabled);
  const isMeetingLocked = useIsMeetingLocked(meetingORM)

  const { crmCustomFields, crmDefaultValues, isLoading } = useCRMFormConfig();

  const meetingFormConfig = isOngoingMeeting ? onGoingMeetingConfig(meetingORM) : beaconConfig
  const staticFields = { ...meetingFormConfig, customValues: defaultValues };

  const usage = { internalUsages: [CustomFieldUsage.MEETING] }

  const separatedAttendees = useMemo(() => getSeparatedAttendees(meetingORM), [meetingORM?.model.attendees]);
  const shouldIncludeCrmFields = canUseMeetingCRMFields && !isOngoingMeeting;
  const shouldShowCustomFields = !!isOngoingMeeting || !isCRMConfigEnabled || canUseMeetingCRMFields;

  const calculatedCustomFields = shouldIncludeCrmFields
    ? [...customFields, ...crmCustomFields]
    // if CRM is enabled but not connected, we should not show the custom fields
    : shouldShowCustomFields
      ? customFields
      : []

  const values = useMemo(() => {
    const values = isOngoingMeeting
      ? initialDefaultValues(meetingORM, customFields)
      : meetingORM?.meta.customValues.configsMap || {};
    const crmCustomValues = getMappedCustomValues(usage, meetingORM?.model.crmRecord?.crmCustomValues, crmCustomFields);

    // lodash merge mutate the first argument, so we need to create a new object
    return merge(cloneDeep(crmDefaultValues), crmCustomValues, values);
  }, [meetingORM, crmCustomFields, canUseMeetingCRMFields]);

  return {
    isLoading: !!isLoading,
    customFields: calculatedCustomFields,
    meetingORM,
    staticFields,
    staticValues: { ...defaultValues, ...separatedAttendees },
    values,
    isReadOnly: isMeetingLocked,
  }
}

export default useMeetingFormConfig;
